import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core'
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { GbDashboardSharedUiModule } from '@galabid/gb-dashboard/shared/ui'
import { TranslateModule } from '@ngx-translate/core'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import { ProjectorsFacade } from '@galabid/gb-dashboard/projector/data-access'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { combineLatest, map, of, startWith } from 'rxjs'
import * as ProjectorContainersActions from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.actions'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import { MatDialogModule } from '@angular/material/dialog'
import * as ProjectorTemplatesActions from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.actions'
import { ProjectorTemplatesFacade } from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.facade'
import { GbPresenterTemplatesListComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-templates-list/gb-presenter-templates-list.component'

@UntilDestroy()
@Component({
  selector: 'gb-add-projector-container-dialog',
  imports: [
    GbDashboardSharedUiModule,
    TranslateModule,
    MatFormFieldModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    GbPresenterTemplatesListComponent,
  ],
  templateUrl: './gb-add-projector-container-dialog.component.html',
  styleUrl: './gb-add-projector-container-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbAddProjectorContainerDialogComponent implements OnDestroy {
  projectorContainerFormGroup = this.formBuilder.group({
    aspect_ratio: ['16:9', [Validators.required]],
    name: ['', [Validators.required]],
    useTemplate: [false],
    template_uuid: [''],
  })

  get aspect_ratio() {
    return this.projectorContainerFormGroup.get('aspect_ratio')
  }

  get name() {
    return this.projectorContainerFormGroup.get('name')
  }

  get useTemplate() {
    return this.projectorContainerFormGroup.get('useTemplate') as FormControl
  }

  get template_uuid() {
    return this.projectorContainerFormGroup.get('template_uuid')
  }

  createEditLoaded$ = this.projectorContainersFacade.projectorContainersEditCreateLoaded$
  selectedTemplate$ = this.projectorTemplatesFacade.selectedProjectorTemplate$

  aspectRatios$ = of([
    { value: '16:9', label: '16:9' },
    { value: '16:10', label: '16:10' },
    { value: '4:3', label: '4:3' },
    { value: '3:2', label: '3:2' },
    { value: '21:9', label: '21:9' },
    { value: '32:9', label: '32:9' },
    { value: '1:1', label: '1:1' },
    { value: '9:16', label: '9:16' },
  ])

  constructor(
    private formBuilder: FormBuilder,
    private projectorContainersFacade: ProjectorContainersFacade,
    private projectorsFacade: ProjectorsFacade,
    private projectorTemplatesFacade: ProjectorTemplatesFacade,
  ) {
    this.projectorsFacade.dispatch(ProjectorActions.loadProjectorTemplates())

    this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.loadProjectorTemplates())

    combineLatest([
      this.selectedTemplate$.pipe(map((template) => template?.uuid || '')),
      this.useTemplate!.valueChanges.pipe(startWith(this.useTemplate?.value)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([templateUuid, useTemplate]) => {
        this.template_uuid?.setValue(useTemplate ? templateUuid : '')
      })
  }

  ngOnDestroy(): void {
    this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.clearSelectedProjectorTemplate())
  }

  onSubmit() {
    if (this.useTemplate?.value && !this.template_uuid?.value) {
      this.projectorContainerFormGroup.markAllAsTouched()
      this.projectorContainerFormGroup.markAsDirty()
      return
    }

    if (!this.projectorContainerFormGroup.valid) {
      this.projectorContainerFormGroup.markAllAsTouched()
      this.projectorContainerFormGroup.markAsDirty()
      return
    }

    this.projectorContainersFacade.dispatch(
      ProjectorContainersActions.createProjectorContainer({
        projectorContainer: {
          name: this.name?.value || '',
          aspect_ratio: this.aspect_ratio?.value || '',
          template_uuid: this.useTemplate?.value ? this.template_uuid?.value || '' : '',
        },
      }),
    )
  }
}
